import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  checkAccountStatus,
  checkOnboardingStatus,
  getRedirectPath,
} from "../lib/utils/redirect-utils";

export default function AuthRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const [whoamiDataVersion, setWhoamiDataVersion] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "whoami_user_data") {
        setWhoamiDataVersion((prev) => prev + 1);
      }
    };

    const handleWhoAmIUpdate = () => {
      setWhoamiDataVersion((prev) => prev + 1);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("whoamiUpdated", handleWhoAmIUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("whoamiUpdated", handleWhoAmIUpdate);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const tokenInQuery =
      searchParams.get("access_token") ||
      searchParams.get("_token") ||
      searchParams.get("refresh_token") ||
      searchParams.get("_refresh");

    if (tokenInQuery) {
      return;
    }

    const accessToken = localStorage.getItem("accessToken");
    const whoamiData = sessionStorage.getItem("whoami_user_data");

    const queryString = searchParams.toString();
    const params = new URLSearchParams(queryString);
    const preservedQuery = params.toString();
    const querySuffix = preservedQuery ? `?${preservedQuery}` : "";

    // If user is authenticated and on home page, redirect them away
    if (accessToken && pathname === "/") {
      // If we have whoami data, use it to determine redirect path
      if (whoamiData) {
        try {
          const parsed = JSON.parse(whoamiData);
          const accountStatus = checkAccountStatus(parsed);
          const onboardingStatus = checkOnboardingStatus(parsed);

          if (accountStatus) {
            const redirectPath = getRedirectPath(
              accountStatus,
              onboardingStatus,
              preservedQuery
            );

            if (redirectPath) {
              const redirectPathOnly = redirectPath.split("?")[0];

              if (pathname !== redirectPathOnly) {
                navigate(redirectPath, { replace: true });
              }
            }
            return;
          }
        } catch {
          // If parsing fails, redirect to waitlist
          const waitlistParams = new URLSearchParams(preservedQuery);
          navigate(`/agent/waitlist?${waitlistParams.toString()}`, { replace: true });
          return;
        }
      }

      // If we have token but no whoami data yet, redirect to agent page as default
      // The whoami data will be fetched and user will be redirected to correct page
      navigate(`/agent${querySuffix}`, { replace: true });
      return;
    }

    if (whoamiData) {
      try {
        const parsed = JSON.parse(whoamiData);
        const accountStatus = checkAccountStatus(parsed);
        const onboardingStatus = checkOnboardingStatus(parsed);

        if (accountStatus) {
          const redirectPath = getRedirectPath(
            accountStatus,
            onboardingStatus,
            preservedQuery
          );

          if (redirectPath) {
            const redirectPathOnly = redirectPath.split("?")[0];

            if (pathname !== redirectPathOnly) {
              navigate(redirectPath, { replace: true });
            }
          }
          return;
        }
      } catch {
        if (pathname !== "/agent/waitlist") {
          const waitlistParams = new URLSearchParams(preservedQuery);
          navigate(`/agent/waitlist?${waitlistParams.toString()}`, { replace: true });
        }
        return;
      }
    }

    if (accessToken && !whoamiData) {
      return;
    }

    if (!accessToken && pathname !== "/") {
      navigate(`/${querySuffix}`, { replace: true });
      return;
    }

    if (!accessToken && pathname === "/") {
      return;
    }
  }, [pathname, navigate, searchParams, whoamiDataVersion]);

  return null;
}
